/**
 * Turning per-chapter programs into one library.
 *
 * The player writes one program per chapter, one file for the theme: zero or
 * more `name = term` definitions, helpers first, with the definitions its tasks
 * ask for among them. Those files are merged into a single namespace, and that
 * is where the interesting rules live:
 *
 * * a name may only be defined once, anywhere;
 * * a name that a task in a chapter asks for belongs to that chapter, and
 *   defining it in another chapter is an error rather than a silent collision;
 * * names beginning with `__` are the test harness's, not the player's;
 * * a name has to be defined before it is used: the files are read in chapter
 *   order, top to bottom, so a name written further down is not available yet.
 *   That one rule is what makes the order the player writes in the order the
 *   program is read in, and it leaves the engine's cycle check nothing to find
 *   in a library this module accepts.
 *
 * A diagnostic also says which level it is *about*, when it is about one: a
 * syntax error is about the chapter, while a duplicate name belongs to the task
 * whose slot it is. That is what lets one bad line in a chapter file leave the
 * tasks that do not touch it checkable.
 *
 * Everything here is pure, so the rules are unit tested rather than discovered
 * by clicking around.
 */

import type { Chapter } from '@spec';
import type { ProgramDefinition, ProgramError } from '../engine/protocol';
import type { EditorDiagnostic } from '../lib/diagnostics';

/** One chapter's program, as the engine parsed it. */
export interface ParsedProgram {
  chapterId: string;
  definitions: ProgramDefinition[];
  /** What could not be read, each failure on its own. */
  errors: ProgramError[];
}

/** Something to show the player about their code. */
export interface Diagnostic extends EditorDiagnostic {
  /**
   * The level whose own definition this is about, or `null` when it is about
   * the chapter itself. Every level in the chapter is shown a chapter-wide
   * diagnostic, but none of them is blocked by it.
   */
  levelId: string | null;
}

/** The merged result. */
export interface Library {
  /** Definition bodies, ready for the engine. */
  definitions: Record<string, string>;
  /** Which chapter defined each name. */
  owners: Map<string, string>;
  /** What each definition references, as the parser saw it. */
  references: Map<string, string[]>;
  /** Problems per chapter. */
  diagnostics: Map<string, Diagnostic[]>;
  /** Names whose definition could not be read, so they are not usable. */
  broken: Set<string>;
  /**
   * Where each name sits in the program: chapters in teaching order, and the
   * definitions of a chapter in the order they are written. It is the order a
   * reader meets them in, which the rule above makes the order they can be
   * used in.
   */
  order: Map<string, number>;
}

/** A library with nothing in it, which is also where every screen starts. */
export function emptyLibrary(): Library {
  return {
    definitions: {},
    owners: new Map(),
    references: new Map(),
    diagnostics: new Map(),
    broken: new Set(),
    order: new Map(),
  };
}

const RESERVED_PREFIX = '__';

/** Which chapter and which level ask for each name, across the whole game. */
export function slotOwners(chapters: Chapter[]): {
  chapters: Map<string, string>;
  levels: Map<string, string>;
} {
  const byChapter = new Map<string, string>();
  const byLevel = new Map<string, string>();
  for (const chapter of chapters) {
    for (const level of chapter.levels) {
      for (const slot of level.slots) {
        byChapter.set(slot.name, chapter.id);
        byLevel.set(slot.name, level.id);
      }
    }
  }
  return { chapters: byChapter, levels: byLevel };
}

function add(map: Map<string, Diagnostic[]>, chapterId: string, diagnostic: Diagnostic): void {
  const existing = map.get(chapterId);
  if (existing === undefined) {
    map.set(chapterId, [diagnostic]);
  } else {
    existing.push(diagnostic);
  }
}

/** Merges the parsed programs, reporting anything that cannot be merged. */
export function buildLibrary(programs: ParsedProgram[], chapters: Chapter[]): Library {
  const definitions: Record<string, string> = {};
  const owners = new Map<string, string>();
  const references = new Map<string, string[]>();
  const spans = new Map<string, { chapterId: string; start: number; end: number }>();
  const diagnostics = new Map<string, Diagnostic[]>();
  const broken = new Set<string>();
  const order = new Map<string, number>();

  const { chapters: askedBy, levels: levelOfName } = slotOwners(chapters);
  const titles = new Map(chapters.map((chapter) => [chapter.id, chapter.title]));
  const title = (chapterId: string): string => titles.get(chapterId) ?? chapterId;

  // A diagnostic about a definition belongs to the task that asks for it, so
  // that a chapter with an unrelated mistake in it stays checkable.
  const levelOf = (name: string): string | null => levelOfName.get(name) ?? null;

  for (const program of programs) {
    // A definition that could not be read is reported on the task that owns
    // its name when the name itself was readable, so a task being written
    // does not spread its problem over the chapter; a line that does not
    // even start with a name could belong anywhere, and stays chapter-wide.
    for (const failure of program.errors) {
      if (failure.name !== undefined) {
        broken.add(failure.name);
      }
      add(diagnostics, program.chapterId, {
        start: failure.start,
        end: failure.end,
        levelId: failure.name === undefined ? null : levelOf(failure.name),
        message: failure.message,
      });
    }
    for (const definition of program.definitions) {
      const { name, start, end } = definition;
      if (name.startsWith(RESERVED_PREFIX)) {
        add(diagnostics, program.chapterId, {
          start,
          end,
          levelId: levelOf(name),
          message: `\`${name}\` is reserved: names beginning with \`${RESERVED_PREFIX}\` belong to the game`,
        });
        continue;
      }
      const previous = owners.get(name);
      if (previous !== undefined) {
        const where = spans.get(name);
        if (where !== undefined) {
          add(diagnostics, where.chapterId, {
            start: where.start,
            end: where.end,
            levelId: levelOf(name),
            message:
              where.chapterId === program.chapterId
                ? `\`${name}\` is defined again below`
                : `\`${name}\` is also defined in the ${title(program.chapterId)} chapter`,
          });
        }
        add(diagnostics, program.chapterId, {
          start,
          end,
          levelId: levelOf(name),
          message:
            previous === program.chapterId
              ? `\`${name}\` is already defined above`
              : `\`${name}\` is already defined in the ${title(previous)} chapter`,
        });
        continue;
      }
      const asked = askedBy.get(name);
      if (asked !== undefined && asked !== program.chapterId) {
        add(diagnostics, program.chapterId, {
          // A name that belongs to another chapter: the player needs to see
          // this wherever they are in this one, so it belongs to the chapter.
          start,
          end,
          levelId: null,
          message: `\`${name}\` is what the ${title(asked)} chapter asks you to define, so it cannot be defined here too`,
        });
        continue;
      }
      definitions[name] = definition.body;
      owners.set(name, program.chapterId);
      references.set(name, definition.references);
      spans.set(name, { chapterId: program.chapterId, start, end });
      order.set(name, order.size);
    }
  }

  // A name that failed to parse somewhere but is defined somewhere else is
  // still usable: the duplicate is what the diagnostics are about, and the
  // levels that use it are not held up by it.
  for (const name of [...broken]) {
    if (name in definitions) {
      broken.delete(name);
    }
  }

  // A reference has to be to something the reader has already met. The files
  // are read in chapter order, top to bottom, so a name defined further down
  // is not available yet, and a name that is not written at all is a typo.
  for (const program of programs) {
    for (const definition of program.definitions) {
      if (owners.get(definition.name) !== program.chapterId) {
        continue;
      }
      const here = order.get(definition.name) ?? 0;
      for (const reference of definition.references) {
        if (reference.startsWith(RESERVED_PREFIX)) {
          continue;
        }
        const there = order.get(reference);
        if (there !== undefined && there >= here) {
          const where = owners.get(reference);
          add(diagnostics, program.chapterId, {
            start: definition.start,
            end: definition.end,
            levelId: levelOf(definition.name),
            message:
              reference === definition.name
                ? `\`${reference}\` is defined in terms of itself`
                : where === program.chapterId
                  ? `\`${reference}\` is used by \`${definition.name}\` before it is defined; move it above`
                  : `\`${reference}\` is used by \`${definition.name}\` before it is defined; it is in the ${title(where ?? '')} chapter`,
          });
          continue;
        }
        if (there !== undefined) {
          continue;
        }
        // A name whose own definition did not parse is a hole rather than a
        // name nobody has written yet, and saying which one is missing saves
        // the player from looking for a task that was never the problem.
        if (broken.has(reference)) {
          add(diagnostics, program.chapterId, {
            start: definition.start,
            end: definition.end,
            levelId: levelOf(definition.name),
            message: `\`${reference}\`, used by \`${definition.name}\`, does not parse`,
          });
          continue;
        }
        // A name some task is going to ask for is a missing prerequisite, which
        // the level list already reports; only a name no task mentions is
        // certainly a mistake.
        if (askedBy.has(reference)) {
          continue;
        }
        add(diagnostics, program.chapterId, {
          start: definition.start,
          end: definition.end,
          levelId: levelOf(definition.name),
          message: `\`${reference}\`, used by \`${definition.name}\`, is not defined`,
        });
      }
    }
  }

  return {
    definitions,
    owners,
    references,
    diagnostics,
    broken,
    order,
  };
}

/**
 * The first name a level depends on whose definition could not be read.
 *
 * A definition that does not parse is in no library at all, so a level that
 * needs it looks like one whose code has changed, or like one still waiting
 * for a prerequisite. Walking the closure finds it wherever it sits, which is
 * what lets the level say what is really wrong instead.
 */
export function brokenDependency(
  roots: Iterable<string>,
  definitions: Record<string, string>,
  references: Map<string, string[]>,
  broken: Set<string>,
): string | null {
  const seen = new Set<string>();
  const pending = [...roots];
  while (pending.length > 0) {
    const name = pending.pop() as string;
    if (seen.has(name)) {
      continue;
    }
    seen.add(name);
    if (broken.has(name)) {
      return name;
    }
    if (name in definitions) {
      pending.push(...(references.get(name) ?? []));
    }
  }
  return null;
}

/**
 * Every definition that the given roots reach, in the order they are written.
 *
 * The roots are whatever a screen needs in scope: a level's required names plus
 * the names its cases mention, or every name in the library. Because a name can
 * only be used below where it is defined, "in the order they are written" is
 * also "dependencies first", which is what makes a list of them readable.
 */
export function contextOf(
  roots: Iterable<string>,
  definitions: Record<string, string>,
  references: Map<string, string[]>,
  order: Map<string, number>,
): string[] {
  const reached = new Set<string>();
  const pending = [...roots];
  while (pending.length > 0) {
    const name = pending.pop() as string;
    if (!(name in definitions) || reached.has(name)) {
      continue;
    }
    reached.add(name);
    pending.push(...(references.get(name) ?? []));
  }
  return [...reached].sort((left, right) => (order.get(left) ?? 0) - (order.get(right) ?? 0));
}

/**
 * Reads stored programs, migrating a save from before chapters had files.
 *
 * A level id as a key is what the old shape looks like; chapter ids are words,
 * so the two cannot be confused. The same object comes back when there is
 * nothing to migrate, so the caller can tell whether to write.
 */
export function readStoredPrograms(
  stored: Record<string, string>,
  chapters: Chapter[],
): Record<string, string> {
  const levelIds = new Set(chapters.flatMap((chapter) => chapter.levels.map((level) => level.id)));
  return Object.keys(stored).some((key) => levelIds.has(key))
    ? migratePrograms(stored, chapters)
    : stored;
}

/**
 * Moves programs saved one-per-level into one file per chapter.
 *
 * Every level's program is appended to its chapter in level order, so the file
 * reads in the order the tasks come. Progress is untouched: a level's cache key
 * is made of the definitions it depends on, not of the program they were
 * written in, so nothing that was solved has to be solved again.
 */
export function migratePrograms(
  stored: Record<string, string>,
  chapters: Chapter[],
): Record<string, string> {
  const programs: Record<string, string> = {};
  const known = new Set(chapters.map((chapter) => chapter.id));
  const append = (chapterId: string, source: string): void => {
    const body = source.trim();
    if (body === '') {
      return;
    }
    const existing = programs[chapterId];
    programs[chapterId] = existing === undefined ? `${body}\n` : `${existing}${body}\n`;
  };

  // Anything already keyed by chapter is left as it is.
  for (const [key, source] of Object.entries(stored)) {
    if (known.has(key)) {
      programs[key] = source;
    }
  }
  for (const chapter of chapters) {
    for (const level of chapter.levels) {
      append(chapter.id, stored[level.id] ?? '');
    }
  }
  return programs;
}
